/**
 * Model-Specific Checklist Items
 * 
 * Additional checks based on car features/type
 */

export const FEATURE_BASED_CHECKS = {
  sunroof: {
    name: 'Sunroof / Moonroof',
    items: [
      {
        id: 'sunroof-1',
        title: 'Sunroof Operation',
        description: 'Test open, close, tilt, and slide functions',
        tip: 'Check for smooth operation without grinding or hesitation',
        whyItMatters: 'Sunroof mechanisms are expensive to repair. Issues can cause leaks and wind noise.',
      },
      {
        id: 'sunroof-2',
        title: 'Sunroof Seal',
        description: 'Check rubber seals around sunroof for integrity',
        tip: 'Look for gaps, cracks, or improper seating',
        whyItMatters: 'Poor seals lead to water leaks that can damage electronics and cause mold.',
        critical: true,
      },
      {
        id: 'sunroof-3',
        title: 'Sunroof Glass',
        description: 'Check glass for scratches, chips, or tint defects',
        tip: 'Inspect from inside and outside in sunlight',
      },
      {
        id: 'sunroof-4',
        title: 'Rain Sensor (if auto-close)',
        description: 'Test automatic close function with water spray',
        tip: 'Some cars close sunroof when rain is detected',
      },
    ],
  },
  
  adas: {
    name: 'ADAS (Advanced Driver Assistance)',
    items: [
      {
        id: 'adas-1',
        title: 'Front Camera Calibration',
        description: 'Verify ADAS camera is properly calibrated',
        tip: 'Check if lane keep assist works on a straight road',
        whyItMatters: 'Miscalibrated ADAS can cause false alerts or fail to warn when needed.',
        critical: true,
      },
      {
        id: 'adas-2',
        title: 'Radar Sensors',
        description: 'Check adaptive cruise and collision warning work',
        tip: 'Test on empty road or service lane during test drive',
        critical: true,
      },
      {
        id: 'adas-3',
        title: 'Lane Departure Warning',
        description: 'Verify LDW system activates correctly',
        tip: 'Should alert when crossing lane without indicator',
      },
      {
        id: 'adas-4',
        title: 'Blind Spot Monitor',
        description: 'Check blind spot warning indicators work',
        tip: 'Should illuminate when vehicle is in blind spot',
      },
      {
        id: 'adas-5',
        title: '360° Camera System',
        description: 'Verify all cameras work and stitching is correct',
        tip: 'Look for misaligned or distorted views',
      },
      {
        id: 'adas-6',
        title: 'Auto Emergency Braking',
        description: 'Confirm AEB system is enabled in settings',
        tip: 'Don\'t test this manually - just verify settings',
        warning: 'Never test AEB towards people or real obstacles',
      },
    ],
  },
  
  hybrid: {
    name: 'Hybrid / Mild-Hybrid System',
    items: [
      {
        id: 'hybrid-1',
        title: 'Hybrid Battery Health',
        description: 'Check battery health indicator shows full capacity',
        tip: 'Ask dealer to show battery diagnostics report',
        whyItMatters: 'Hybrid batteries are expensive. Degraded batteries affect mileage and resale.',
        critical: true,
      },
      {
        id: 'hybrid-2',
        title: 'EV Mode Operation',
        description: 'Test EV-only mode if available',
        tip: 'Should run on battery alone at low speeds',
      },
      {
        id: 'hybrid-3',
        title: 'Regenerative Braking',
        description: 'Verify regen braking charges battery when decelerating',
        tip: 'Watch power flow indicator on dashboard',
      },
      {
        id: 'hybrid-4',
        title: 'Engine Start-Stop Transition',
        description: 'Check smooth transition between EV and engine',
        tip: 'Should be near-seamless without jerks',
      },
      {
        id: 'hybrid-5',
        title: 'Charging Port (if PHEV)',
        description: 'Test charging port opens and cable connects',
        tip: 'Check port illumination at night',
      },
    ],
  },
  
  electric: {
    name: 'Electric Vehicle',
    items: [
      {
        id: 'ev-1',
        title: 'Battery State of Health',
        description: 'Request battery health report showing capacity',
        tip: 'Should show 100% or very close for new vehicle',
        whyItMatters: 'Battery is the most expensive EV component. Verify it\'s in perfect condition.',
        critical: true,
      },
      {
        id: 'ev-2',
        title: 'Charging - AC (Slow)',
        description: 'Test AC charging port and cable connection',
        tip: 'Verify charging initiates and displays correctly',
        critical: true,
      },
      {
        id: 'ev-3',
        title: 'Charging - DC (Fast)',
        description: 'If possible, test DC fast charging capability',
        tip: 'Verify maximum charging rate is as specified',
      },
      {
        id: 'ev-4',
        title: 'Range Display Accuracy',
        description: 'Check if displayed range matches specifications',
        tip: 'Full charge should show close to ARAI certified range',
      },
      {
        id: 'ev-5',
        title: 'Charging Cable & Adapter',
        description: 'Verify all charging cables and adapters included',
        tip: 'Check for portable charger, home charger cable',
        critical: true,
      },
      {
        id: 'ev-6',
        title: 'Pre-conditioning Function',
        description: 'Test battery/cabin pre-conditioning via app',
        tip: 'Should work while connected to charger',
      },
      {
        id: 'ev-7',
        title: 'One-Pedal Driving Mode',
        description: 'Test regenerative braking intensity settings',
        tip: 'Should bring car to complete stop in max regen mode',
      },
    ],
  },
  
  diesel: {
    name: 'Diesel Engine',
    items: [
      {
        id: 'diesel-1',
        title: 'DPF System',
        description: 'Check DPF (Diesel Particulate Filter) warning light',
        tip: 'No warning should appear on a new diesel',
        whyItMatters: 'DPF issues are expensive to fix and common in diesels.',
      },
      {
        id: 'diesel-2',
        title: 'AdBlue Level (if equipped)',
        description: 'Verify AdBlue tank is filled',
        tip: 'Low AdBlue can limit engine power',
      },
      {
        id: 'diesel-3',
        title: 'Cold Start Behavior',
        description: 'Diesel should start smoothly even when cold',
        tip: 'Some clatter is normal for diesel but should settle quickly',
      },
      {
        id: 'diesel-4',
        title: 'Turbo Spool',
        description: 'Check turbo spools smoothly without lag or surge',
        tip: 'Should feel progressive power delivery',
      },
      {
        id: 'diesel-5',
        title: 'Exhaust Smoke',
        description: 'Check for excessive smoke on acceleration',
        tip: 'Black smoke indicates fueling issue, blue smoke is oil burning',
        critical: true,
      },
    ],
  },
  
  turbo: {
    name: 'Turbo/Supercharged Engine',
    items: [
      {
        id: 'turbo-1',
        title: 'Turbo Whine',
        description: 'Listen for normal turbo spool sound',
        tip: 'Should be a smooth whistle, not grinding or screeching',
        whyItMatters: 'Abnormal turbo sounds indicate potential failure.',
      },
      {
        id: 'turbo-2',
        title: 'Boost Pressure',
        description: 'If gauge equipped, verify boost builds correctly',
        tip: 'Should build smoothly and hold under load',
      },
      {
        id: 'turbo-3',
        title: 'Wastegate Operation',
        description: 'No fluttering or surging under throttle',
        tip: 'Boost should be smooth, not spiky',
      },
      {
        id: 'turbo-4',
        title: 'Intercooler Check',
        description: 'Verify intercooler is properly mounted (visual)',
        tip: 'Check for obvious damage or loose connections',
      },
    ],
  },
  
  automatic: {
    name: 'Automatic Transmission',
    items: [
      {
        id: 'auto-1',
        title: 'Gear Shift Quality',
        description: 'Check shifts are smooth without jerks',
        tip: 'Test in D, Sport, and Manual mode if available',
        whyItMatters: 'Rough shifts can indicate transmission issues.',
      },
      {
        id: 'auto-2',
        title: 'Kickdown Response',
        description: 'Test full throttle downshift response',
        tip: 'Should downshift promptly for overtaking',
      },
      {
        id: 'auto-3',
        title: 'Creep Function',
        description: 'Car should creep forward when brake released (torque converter)',
        tip: 'DCT/AMT may not creep - verify based on type',
      },
      {
        id: 'auto-4',
        title: 'Hill Hold Assist',
        description: 'Test car holds on incline without rolling back',
        tip: 'Should hold for a few seconds after releasing brake',
      },
      {
        id: 'auto-5',
        title: 'Paddle Shifters (if equipped)',
        description: 'Test paddle shifter response',
        tip: 'Should shift promptly up and down',
      },
    ],
  },
  
  awd: {
    name: 'AWD / 4WD System',
    items: [
      {
        id: 'awd-1',
        title: '4WD Indicator',
        description: 'Verify 4WD/AWD mode indicator works correctly',
        tip: 'Check different drive modes if available',
      },
      {
        id: 'awd-2',
        title: 'Transfer Case (if applicable)',
        description: 'Test 4H, 4L modes engage smoothly',
        tip: 'Follow manual for proper engagement procedure',
      },
      {
        id: 'awd-3',
        title: 'Diff Lock (if equipped)',
        description: 'Verify differential lock engages',
        tip: 'Indicator should show when activated',
      },
      {
        id: 'awd-4',
        title: 'Power Distribution Display',
        description: 'Check real-time AWD torque display if available',
        tip: 'Should show power going to wheels during acceleration',
      },
    ],
  },
  
  premium_audio: {
    name: 'Premium Audio System',
    items: [
      {
        id: 'audio-1',
        title: 'All Speakers Working',
        description: 'Use balance/fade to test each speaker zone',
        tip: 'Check tweeters, mid-range, and subwoofer separately',
      },
      {
        id: 'audio-2',
        title: 'Subwoofer Performance',
        description: 'Test bass response with bass-heavy track',
        tip: 'Should be deep and clean without distortion',
      },
      {
        id: 'audio-3',
        title: 'No Rattles at Volume',
        description: 'Play at high volume and check for panel rattles',
        tip: 'Rattles indicate poor panel fitment',
      },
      {
        id: 'audio-4',
        title: 'Audio Settings',
        description: 'Verify EQ and sound modes work correctly',
        tip: 'Test different presets and custom EQ',
      },
    ],
  },
  
  leather_interior: {
    name: 'Leather / Premium Interior',
    items: [
      {
        id: 'leather-1',
        title: 'Leather Seat Condition',
        description: 'Check for scratches, creases, or dye transfer',
        tip: 'New leather should be uniform without blemishes',
      },
      {
        id: 'leather-2',
        title: 'Stitching Quality',
        description: 'Inspect stitching on seats and steering wheel',
        tip: 'Should be uniform with no loose threads',
      },
      {
        id: 'leather-3',
        title: 'Leather Smell',
        description: 'Genuine leather should have characteristic smell',
        tip: 'Leatherette/synthetic has different smell',
      },
      {
        id: 'leather-4',
        title: 'Ventilated Seats (if equipped)',
        description: 'Test seat cooling function works',
        tip: 'Should feel airflow through perforations',
      },
      {
        id: 'leather-5',
        title: 'Heated Seats (if equipped)',
        description: 'Test seat heating on all levels',
        tip: 'Should warm up within 2-3 minutes',
      },
    ],
  },
};

/**
 * Get feature checks based on selected features
 */
export const getFeatureChecks = (selectedFeatures) => {
  const checks = [];
  
  selectedFeatures.forEach(feature => {
    if (FEATURE_BASED_CHECKS[feature]) {
      checks.push({
        featureId: feature,
        ...FEATURE_BASED_CHECKS[feature],
      });
    }
  });
  
  return checks;
};

/**
 * Get all available features
 */
export const getAllFeatures = () => {
  return Object.entries(FEATURE_BASED_CHECKS).map(([key, value]) => ({
    id: key,
    name: value.name,
    itemCount: value.items.length,
  }));
};

export default FEATURE_BASED_CHECKS;
